import type { Application } from '@hotwired/stimulus';

import registerLazy from './register-lazy';

import AccountMenu from '../controllers/account-menu-controller';
import Form from '../controllers/form-controller';
import FormMoney from '../controllers/form-money-controller';
import FormSlider from '../controllers/form-slider-controller';
import Layout from '../controllers/layout-controller';
import LiveComponent from '../controllers/live-component-controller';
import OnboardingHeader from '../controllers/onboarding-header-controller';
import Toc from '../controllers/toc-controller';

/**
 * Contrôleurs Stimulus communs aux entrées `public` et `account`.
 *
 * Y figure tout ce qu'un gabarit commun aux espaces déclare — donc tout ce dont une page partagée
 * (`templates/shared/pages/**`) peut avoir besoin, puisqu'elle est servie à l'identique dans les
 * trois espaces (cf. `App\Enum\Space`).
 *
 * Chaque enregistrement est commenté du gabarit qui le déclare : c'est ce qui permet de savoir,
 * plus tard, quand un contrôleur n'a plus rien à faire ici.
 */
export default function registerSharedControllers (application: Application): void {
  // layout/base.html.twig : <body data-controller="layout">
  application.register('layout', Layout);

  // layout/header.html.twig
  application.register('account-menu', AccountMenu);
  application.register('onboarding-header', OnboardingHeader);

  // layout/form_theme.html.twig : thème de formulaire global (config/packages/twig.yaml)
  application.register('form', Form);

  // components/slider.html.twig + blocs money/stepper de public/layout/form_theme.html.twig
  application.register('form-slider', FormSlider);

  // components/content-with-toc.html.twig : pages de contenu, dont les pages partagées
  application.register('toc', Toc);

  // public/contact_component.html.twig (page partagée) et composants Live de l'espace client
  application.register('live-component', LiveComponent);

  // simulateur/epargne-assurance-vie/component.html.twig, monté en public comme en client
  application.register('form-money', FormMoney);

  /*
   * Différés (cf. register-lazy.ts) : dépendances lourdes déclarées depuis des gabarits communs,
   * donc impossibles à cantonner à une entrée, mais utilisées par une poignée d'écrans seulement.
   */

  // blocs date des deux thèmes (layout/ et public/layout/form_theme.html.twig)
  // + Form\Account\Subscription\Steps\Common\CoordonneesBancairesType — imask
  registerLazy(application, 'form-input-mask', () => import('../controllers/form-input-mask-controller'));

  // Form\Type\PhoneType, notamment via ContactType (page de contact partagée) — intl-tel-input
  registerLazy(application, 'phone-input', () => import('../controllers/phone-input-controller'));

  // simulateur/epargne-assurance-vie/component.html.twig — apexcharts
  registerLazy(
    application,
    'simulateur--epargne-assurance-vie-chart',
    () => import('../controllers/simulateur/epargne-assurance-vie-chart-controller'),
  );
}
